import React, { Component } from "react";
import {
  DisplaySmall,
  Body,
  Button,
  ButtonGroup,
  IconButton,
  Markdown,
  RemoveSmall,
} from "@conserveit/blocks";
import PropTypes from "prop-types";
import { I18n } from "@lingui/react";
import { Trans } from "@lingui/macro";
import { Link } from "react-router-dom";
import moment from "moment";
import Loading from "../../../Loading";
import { EquipPreview } from "../../../EquipPreview";

// Legacy AddComment
import IssueModals from "../../../IssueModals";
import AddComment from "../../../../containers/AddComment/AddComment";

import {
  getStatusMap,
  getResponsibleMap,
  getContextMap,
} from "../../../Action/utils/lists";
import Comment from "../Comment";
import { parseId } from "../ListRow/ListRow.utils";

import {
  Container,
  Header,
  EditLink,
  SiteTitle,
  Content,
  Section,
  SectionGrid,
  SavingsCard,
  SectionHeading,
  EquipList,
  WrapLegacyAddComment,
  CreatedUpdatedText,
} from "./IssuePane.styles";
import { history } from "../../../../browserHistory";

const DATE_FORMAT = "DD/MM/YY";

class IssuePane extends Component {
  static propTypes = {
    issue: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    userAccess: PropTypes.array.isRequired,
  };

  static defaultProps = {
    comments: [],
  };

  get hasAnySavings() {
    return (
      this.hasSavings("elec") ||
      this.hasSavings("gas") ||
      this.hasSavings("operational_savings") ||
      this.hasSavings("total_financial_impact") ||
      this.hasSavings("water")
    );
  }

  hasSavings = (savingsKey) => {
    const { issue } = this.props;
    return issue[savingsKey] !== "" && issue[savingsKey] > 0;
  };

  render() {
    const { issue, isLoading, comments, userAccess, equipRules = [] } = this.props;

    return (
      <I18n>
        {({ i18n }) => (
          <Container data-cy="Actions-issuePanel">
            <Header>
              {!isLoading && (
                <DisplaySmall>
                  {`#${parseId(issue.key)}`}{" "}
                  <EditLink as={Link} to={`/actions/${issue.key}/edit`} hidden={!userAccess.includes("editIssue")}>
                    <Trans id="issue_pane.edit">Edit</Trans>
                  </EditLink>
                </DisplaySmall>
              )}
              {isLoading && <Loading />}
              <IconButton onClick={this.props.onClose} icon={<RemoveSmall />} />
            </Header>
            {!isLoading && (
              <Content>
                <Section>
                  <SiteTitle>
                    {issue.components} <span>{issue.project_name}</span>
                  </SiteTitle>
                  <CreatedUpdatedText>
                    <Trans id="issue_pane.created">Created</Trans>{" "}
                    {moment(issue.created).format(DATE_FORMAT)}{" "}
                    <Trans id="issue_pane.updated">Updated</Trans>{" "}
                    {moment(issue.updated).format(DATE_FORMAT)}
                  </CreatedUpdatedText>
                  <SectionHeading>
                    <Trans id="issue_pane.status">Status</Trans>
                  </SectionHeading>
                  <Body>
                    {getStatusMap(i18n)[issue.bonfire_status]}{" "}
                    <Link to={`/actions/${issue.key}/edit`} hidden={!userAccess.includes("editIssue")}>
                      <Trans id="issue_pane.change">(change)</Trans>
                    </Link>
                  </Body>
                </Section>

                <SectionGrid>
                  <Section>
                    <SectionHeading>
                      <Trans id="issue_pane.type">Type</Trans>
                    </SectionHeading>
                    <Body>{getContextMap(i18n)[issue.context]}</Body>
                  </Section>

                  <Section>
                    <SectionHeading>
                      <Trans id="issue_pane.responsible">Responsible</Trans>
                    </SectionHeading>
                    <Body>
                      {
                        getResponsibleMap(i18n, issue.project_key === "WOOL")[
                          issue.responsible
                        ]
                      }
                    </Body>
                  </Section>

                  {issue.insite_assignee && (
                    <Section>
                      <SectionHeading>
                        <Trans id="issue_pane.assignee">Assignee</Trans>
                      </SectionHeading>
                      <Body>{issue.insite_assignee}</Body>
                    </Section>
                  )}
                </SectionGrid>

                {this.hasAnySavings && (
                  <SavingsCard>
                    {(this.hasSavings("elec") ||
                      this.hasSavings("gas") ||
                      this.hasSavings("water")) && (
                      <div>
                        <SectionHeading>
                          <Trans id="issue_pane.energy_savings">
                            Energy Savings
                          </Trans>
                        </SectionHeading>
                        {this.hasSavings("elec") && (
                          <p>
                            <Trans id="issue_pane.electricity">
                              Electricity: {issue.elec}kWh
                            </Trans>
                          </p>
                        )}
                        {this.hasSavings("gas") && (
                          <p>
                            <Trans id="issue_pane.gas">
                              Gas: {issue.gas}GJ
                            </Trans>
                          </p>
                        )}
                        {this.hasSavings("water") && (
                          <p>
                            <Trans id="issue_pane.water">
                              Water: {issue.water}kL
                            </Trans>
                          </p>
                        )}
                        {this.hasSavings("total_financial_impact") && (
                          <p>
                            <Trans id="issue_pane.estimated">
                              Estimated: ${issue.total_financial_impact}
                            </Trans>
                          </p>
                        )}
                      </div>
                    )}
                    {this.hasSavings("operational_savings") && (
                      <div>
                        <SectionHeading>
                          <Trans id="issue_pane.operational_savings">
                            Operational Savings
                          </Trans>
                        </SectionHeading>
                        <p>
                          <Trans id="issue_pane.estimated">
                            Estimated: ${issue.operational_savings}
                          </Trans>
                        </p>
                      </div>
                    )}
                  </SavingsCard>
                )}

                {issue.description && (
                  <Section>
                    <SectionHeading>
                      <Trans id="issue_pane.description">Description</Trans>
                    </SectionHeading>
                    <Body as="div">
                      <Markdown source={issue.description} />
                    </Body>
                  </Section>
                )}

                <Section>
                  <ButtonGroup tight>
                    {issue.screenshot_link && (
                      <Button
                        wafer
                        as="a"
                        target="_blank"
                        rel="noopener"
                        href={issue.screenshot_link}
                      >
                        <Trans id="issue_pane.annotation">Annotation</Trans>
                      </Button>
                    )}

                    {!issue.screenshot_link && (
                      <Button
                        wafer
                        appearance="subdued"
                        hidden={!userAccess.includes("editIssue")}
                        onClick={() =>
                          history.push(`/actions/${issue.key}/edit`)
                        }
                      >
                        <Trans id="issue_pane.add_annotation">
                          Add Annotation
                        </Trans>
                      </Button>
                    )}

                    {issue.skyspark_link && (
                      <Button
                        wafer
                        as="a"
                        target="_blank"
                        rel="noopener"
                        href={issue.skyspark_link}
                      >
                        <Trans id="issue_pane.trend_graph">Trend Graph</Trans>
                      </Button>
                    )}

                    {!issue.skyspark_link && (
                      <Button
                        wafer
                        appearance="subdued"
                        hidden={!userAccess.includes("editIssue")}
                        onClick={() =>
                          history.push(`/actions/${issue.key}/edit`)
                        }
                      >
                        <Trans id="issue_pane.add_analytics">
                          Add Trend Graph
                        </Trans>
                      </Button>
                    )}
                  </ButtonGroup>
                </Section>

                {(equipRules.length > 0 || issue.target !== "") && (
                  <Section>
                    <SectionHeading>
                      <Trans id="issue_pane.equips">Equips</Trans>
                    </SectionHeading>
                    <EquipList>
                      {equipRules.length > 0 &&
                        equipRules.map((item) => (
                          <li key={item.id}>
                            <EquipPreview data={item}>
                              {item.targetName}
                            </EquipPreview>
                          </li>
                        ))}
                      {issue.target && (
                        <Body as="div">
                          <Markdown>{issue.target}</Markdown>
                        </Body>
                      )}
                    </EquipList>
                  </Section>
                )}

                {/* TODO: These components will be rebuilt */}
                <IssueModals />
                <Section hidden={!userAccess.includes("editIssue")}>
                  <SectionHeading>
                    <Trans id="issue_pane.add_comment">Add Comment</Trans>
                  </SectionHeading>
                  <WrapLegacyAddComment>
                    <AddComment selected={issue} />
                  </WrapLegacyAddComment>
                </Section>

                {comments && comments.length > 0 && (
                  <Section data-cy="IssuePane-Comments">
                    <SectionHeading>
                      <Trans id="issue_pane.comments">Comments</Trans>
                    </SectionHeading>

                    {comments &&
                      [...comments]
                        // TODO: This should be sorted in reverse by the API
                        // TODO: remove comment_id/author_name_display after v2 comments api ready
                        .reverse()
                        .map((comment) => (
                          <Comment
                            key={comment.comment_id || comment.id}
                            author={
                              comment.author_name_display || comment.author
                            }
                            body={comment.body}
                            createdAt={comment.created}
                            updatedAt={comment.updated}
                          />
                        ))}
                  </Section>
                )}
              </Content>
            )}
          </Container>
        )}
      </I18n>
    );
  }
}

export default IssuePane;
